/*
 * kp-countdown.js — keeps a <kp-countdown> true after Liquid drew it.
 *
 * The end is data-ends, an ISO-8601 string with its offset (see
 * snippets/kp-campaign-ends.liquid), so Date.parse reads the same instant
 * the server did. At zero the clock does not sit at 00:00:00:00 — the
 * Guideline says removed, never zeroed — it takes the nearest
 * [data-kp-countdown-host] with it, or just itself.
 *
 * Unless data-then names what comes next (#67): a JSON list of
 * {at, label} the clock walks in order, taking each entry's end and label
 * at zero — the campaign bar counts to the launch event's next milestone
 * this way. A page served from cache after a milestone passed skips the
 * entries already behind it on connect, so it never shows a stale phase.
 *
 * IIFE + customElements.get(): the file can land on a page twice (PDP block
 * and campaign bar) and stay harmless.
 */
(() => {
  class KpCountdown extends HTMLElement {
    connectedCallback() {
      this.end = Date.parse(this.dataset.ends);
      this.queue = this.readQueue();
      this.labelEl = this.querySelector('[data-kp-cd-label]');
      if (Number.isNaN(this.end) && !this.advance()) return this.expire();
      this.cells = {};
      this.querySelectorAll('[data-kp-cd]').forEach((el) => {
        this.cells[el.dataset.kpCd] = el;
      });
      this.tick();
      this.timer = setInterval(() => this.tick(), 1000);
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }

    tick() {
      const left = this.end - Date.now();
      if (left <= 0) {
        if (this.advance()) return this.tick();
        clearInterval(this.timer);
        return this.expire();
      }
      const pad = (n) => String(n).padStart(2, '0');
      this.set('d', Math.floor(left / 864e5));
      this.set('h', pad(Math.floor(left / 36e5) % 24));
      this.set('m', pad(Math.floor(left / 6e4) % 60));
      this.set('s', pad(Math.floor(left / 1e3) % 60));
    }

    readQueue() {
      try {
        const list = JSON.parse(this.dataset.then || '[]');
        return Array.isArray(list) ? list : [];
      } catch (error) {
        return [];
      }
    }

    /* Moves to the first queued end still ahead; false when none is left. */
    advance() {
      while (this.queue.length) {
        const next = this.queue.shift();
        const end = Date.parse(next.at);
        if (Number.isNaN(end) || end <= Date.now()) continue;
        this.end = end;
        if (this.labelEl && next.label) this.labelEl.textContent = next.label;
        return true;
      }
      return false;
    }

    set(key, value) {
      const el = this.cells[key];
      if (el && el.textContent !== String(value)) el.textContent = value;
    }

    expire() {
      (this.closest('[data-kp-countdown-host]') || this).remove();
    }
  }

  if (!customElements.get('kp-countdown')) {
    customElements.define('kp-countdown', KpCountdown);
  }
})();
